import React, { useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { getGeoCode } from 'utils/places';
import {
  CircularProgress, InputAdornment, Stack, TextField,
  Typography,
} from '@mui/material';
import Icon from 'components/Icon';
import useAutoComplete from './useAutoComplete';

function SearchLocationInput({
  onChange, onSelectingLocation, value, ...props
}) {
  const [open, setOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const { options, loading } = useAutoComplete(searchQuery);
  const active = open && options.length > 0;

  useEffect(() => {
    if (!onSelectingLocation) {
      return;
    }
    onSelectingLocation(active);
    return () => onSelectingLocation(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [active]);

  function getEndAdornment() {
    if (searchQuery) {
      if (isFocused || open) {
        return (
          <Icon
            sx={{
              cursor: 'pointer', border: 'none', bgcolor: 'transparent', p: 0,
            }}
            component="button"
            size={20}
            name="close-outlined"
            onClick={() => isFocused && setSearchQuery('')}
          />
        );
      }
      return (
        <Icon
          sx={{ cursor: 'pointer', color: 'text.primary' }}
          size={20}
          name="edit-outlined"
        />
      );
    }
    if (isFocused) {
      return null;
    }
    return (
      <Stack direction="row" alignItems="center" color="text.primary">
        <Typography component="span" variant="body-md">Select</Typography>
        <Icon name="arrow-right" size={18} />
      </Stack>
    );
  }

  async function handleSelect(_, selectedOption) {
    if (!selectedOption || typeof selectedOption !== 'object') {
      return;
    }
    const {
      place_id: placeId,
      description: text,
      inaccurate,
    } = selectedOption;
    setSearchQuery(text);
    document.activeElement?.blur();
    const { lat, lng } = await getGeoCode(placeId);
    const center = {
      lat, lng, text, inaccurate,
    };
    onChange(center);
  }

  return (
    <Autocomplete
      freeSolo
      open={active}
      clearIcon={null}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      componentsProps={{
        paper: {
          elevation: 0,
          sx: (theme) => ({
            borderTopLeftRadius: 0,
            borderTopRightRadius: 0,
            mt: 0,
            p: 0,
            bgcolor: theme.palette.background.elevation1,
            '& .MuiAutocomplete-listbox': {
              p: 0,
              '& .MuiAutocomplete-option': {
                p: 2,
                borderBottom: `1px solid ${theme.palette.border.default}`,
                ...theme.typography['body-md'],
                '&:last-child': {
                  borderBottom: 'none',
                },
                '& .Mui-focused, &:hover': {
                  bgcolor: theme.palette.background.elevation2,
                },
              },
            },
          }),
        },
      }}
      getOptionLabel={(option) => option.description || searchQuery}
      filterOptions={(o) => o}
      options={options}
      onChange={handleSelect}
      inputValue={searchQuery}
      renderInput={({ InputProps, ...inputProps }) => (
        <TextField
          onChange={({ target: { value } }) => setSearchQuery(value)}
          placeholder="Search for a location"
          variant="filled"
          size="small"
          onFocus={() => setIsFocused(true)}
          onBlur={() => setIsFocused(false)}
          sx={{
            '& .MuiInputBase-root': {
              px: 2,
              py: 1.5,
              pr: '16px !important',
              border: 'none',
              bgcolor: 'background.elevation1',
              '&::before,&::after': {
                display: 'none',
              },
              '& .MuiInputBase-input': {
                p: 0,
              },
              borderRadius: 3,
              ...(active ? {
                borderBottomLeftRadius: 0,
                borderBottomRightRadius: 0,
              } : {}),
            },
          }}
          InputProps={{
            ...InputProps,
            startAdornment: (
              <InputAdornment position="start" sx={{ mr: 1, mt: '0 !important' }}>
                {loading && open ? (
                  <CircularProgress size={24} />
                ) : (
                  <Icon
                    name={value?.lat ? 'location-filled' : 'location-outlined'}
                    size={24}
                    color="text.primary"
                  />
                )}
              </InputAdornment>
            ),
            endAdornment: (
              <InputAdornment position="end">
                {getEndAdornment()}
              </InputAdornment>
            ),
          }}
          {...inputProps}
        />
      )}
      forcePopupIcon={false}
      {...props}
    />
  );
}

export default SearchLocationInput;
